import React, { useEffect, useState } from "react";
import { getUserAttendanceForMonth } from "../../services/attendanceService";
import { publicHolidays } from "../../data/publicHolidays";
// src/pages/tutor/TutorDashboard.js
import { useAuth } from "../../context/AuthProvider";
import { collection, query, where, getDocs, addDoc } from "firebase/firestore";
import { db } from "../../firebase";
import StatsCard from "../../components/StatsCard";
import CompactStatsCard from "../../components/CompactStatsCard";
import TutorChart from "./TutorChart";
import { getCollegeStatistics } from "../../services/collegeService";
import { getRelevantActivities } from "../../services/activityService";
import SalaryCard from "../../components/SalaryCard";
import { Card, Row, Col, ListGroup, Badge, Modal } from "react-bootstrap";
import { Clock, User, CheckCircle, AlertCircle, FileText, LogIn, LogOut } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import Loader from "../../components/Loader";
import Skills from "../Skills";
import LeaveApplication from "../../components/LeaveApplication";
import { getSkills } from "../../services/skillService";
import { getLeavesByUserId, getLeaveStats, applyForLeave, cancelLeave, LEAVE_TYPES, LEAVE_STATUS, calculateLeaveDays } from "../../services/leaveService";
import { toast } from "react-toastify";
import { XIcon } from "lucide-react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import { updateUser } from "../../services/userService";
import { logActivity } from "../../services/activityService";

export default function TutorDashboard() {
  const { user } = useAuth();
  // Public Holidays logic
  const [upcomingHoliday, setUpcomingHoliday] = useState(null);
  const [todayHoliday, setTodayHoliday] = useState(null);
  const [allHolidays, setAllHolidays] = useState([]);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [stats, setStats] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [courses, setCourses] = useState([]);
  const [collegeStats, setCollegeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collegeLoading, setCollegeLoading] = useState(true);
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [lastCheckIn, setLastCheckIn] = useState(null);
  const [skills, setSkills] = useState(user?.skills || []);
  const [allSkills, setAllSkills] = useState([]);
  const [newSkill, setNewSkill] = useState("");
  const [showQuickLeaveForm, setShowQuickLeaveForm] = useState(false);
  const [quickLeaveLoading, setQuickLeaveLoading] = useState(false);
  const [cancelingLeaveId, setCancelingLeaveId] = useState(null);
  const [leaveStats, setLeaveStats] = useState(null);
  const [userLeaves, setUserLeaves] = useState([]);
  const [leaveFormData, setLeaveFormData] = useState({
    leaveType: LEAVE_TYPES.PERSONAL,
    startDate: "",
    endDate: "",
    reason: "",
  });
  // Monthly attendance state
  const [monthlyAttendance, setMonthlyAttendance] = useState([]);
  const [attendanceMonth, setAttendanceMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  useEffect(() => {
    // Filter holidays for current year
    const year = new Date().getFullYear();
    const holidays = publicHolidays.filter(h => h.date.startsWith(year.toString()));
    setAllHolidays(holidays);

    // Show info message one day before a holiday and on the holiday
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().slice(0, 10);
    const foundTomorrow = holidays.find(h => h.date === tomorrowStr);
    const foundToday = holidays.find(h => h.date === todayStr);
    setUpcomingHoliday(foundTomorrow || null);
    setTodayHoliday(foundToday || null);
  }, []);

  // Fetch monthly attendance
  const fetchMonthlyAttendance = async () => {
    if (user?.id && attendanceMonth) {
      const [year, month] = attendanceMonth.split("-").map(Number);
      try {
        const records = await getUserAttendanceForMonth(user.id, month, year);
        setMonthlyAttendance(records);
      } catch (e) {
        setMonthlyAttendance([]);
      }
    }
  };

  const fetchAllSkills = async () => {
    const skillsList = await getSkills();
    setAllSkills(skillsList);
  };

  const fetchLeaveData = async () => {
    if (!user?.id) return;
    try {
      const [leaves, stats] = await Promise.all([
        getLeavesByUserId(user.id),
        getLeaveStats(user.id),
      ]);
      setUserLeaves(leaves);
      setLeaveStats(stats);
    } catch (error) {
      console.error("Error fetching leave data:", error);
    }
  };

  // Fetch all tasks assigned to this tutor
  const fetchTasks = async () => {
    const tasksSnap = await getDocs(collection(db, "tasks"));
    const myTasks = tasksSnap.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((t) => t.assignedTo === user.id);
    setTasks(myTasks);
  };

  // Fetch all projects assigned to this tutor
  const fetchProjects = async () => {
    const projectsSnap = await getDocs(collection(db, "projects"));
    const myProjects = projectsSnap.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((p) => (p.assignedTutors || []).includes(user.id));
    setProjects(myProjects);
  };

  // Fetch all courses assigned to this tutor
  const fetchCoursesList = async () => {
    const coursesSnap = await getDocs(
      query(collection(db, "courses"), where("tutorId", "==", user.id))
    );
    setCourses(coursesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
  };

  useEffect(() => {
    fetchTutorStats();
    fetchCollegeStats();
    fetchRecentActivities();
    fetchCheckInStatus();
    fetchAllSkills();
    fetchLeaveData();
    fetchMonthlyAttendance();
    fetchTasks();
    fetchProjects();
    fetchCoursesList();
  }, [user, attendanceMonth]);
  // --- Monthly Attendance Table ---
  const renderMonthlyAttendanceTable = () => (
    <div className="mb-4">
      <h5>Monthly Attendance</h5>
      <div className="mb-2">
        <label htmlFor="attendanceMonth">Select Month: </label>
        <input
          id="attendanceMonth"
          type="month"
          value={attendanceMonth}
          onChange={e => setAttendanceMonth(e.target.value)}
        />
      </div>
      <div className="table-responsive">
        <table className="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Hours</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {monthlyAttendance.length === 0 ? (
              <tr><td colSpan="5" className="text-center">No records</td></tr>
            ) : (
              monthlyAttendance.map((rec) => {
                const needsRegularise = !rec.checkIn || !rec.checkOut || (rec.totalHours || 0) < 8;
                return (
                  <tr key={rec.id}>
                    <td>{rec.date}</td>
                    <td>{rec.checkIn ? new Date(rec.checkIn).toLocaleTimeString() : "-"}</td>
                    <td>{rec.checkOut ? new Date(rec.checkOut).toLocaleTimeString() : "-"}</td>
                    <td>{rec.totalHours ? rec.totalHours.toFixed(2) : "-"}</td>
                    <td>
                      {needsRegularise ? (
                        <span className="text-danger">Irregular</span>
                      ) : (
                        <span className="text-success">OK</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const fetchTutorStats = async () => {
    if (!user || !user.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const coursesSnap = await getDocs(
      query(collection(db, "courses"), where("tutorId", "==", user.id))
    );
    const tasksSnap = await getDocs(collection(db, "tasks"));
    const tutorTasks = tasksSnap.docs.filter(
      (d) => d.data().assignedTo === user.id
    );

    setStats([
      {
        title: "Courses Assigned",
        value: coursesSnap.size,
        color: "bg-indigo-500",
      },
      {
        title: "Tasks Pending",
        value: tutorTasks.filter((t) => t.data().status === "pending").length,
        color: "bg-pink-500",
      },
      {
        title: "Tasks Completed",
        value: tutorTasks.filter((t) => t.data().status === "completed")
          .length,
        color: "bg-green-500",
      },
    ]);

    setLoading(false);
  };

  const fetchCollegeStats = async () => {
    setCollegeLoading(true);
    try {
      const stats = await getCollegeStatistics();
      setCollegeStats(stats);
    } catch (error) {
      console.error("Error fetching college statistics:", error);
      setCollegeStats(null);
    }
    setCollegeLoading(false);
  };

  const fetchRecentActivities = async () => {
    setActivitiesLoading(true);
    try {
      if (user?.id) {
        const activities = await getRelevantActivities(user.id, "tutor", 7);
        setRecentActivities(activities);
      }
    } catch (error) {
      console.error("Error fetching recent activities:", error);
      setRecentActivities([]);
    }
    setActivitiesLoading(false);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "task_completed":
        return <CheckCircle size={16} className="text-green-500" />;
      case "task_assigned":
        return <FileText size={16} className="text-blue-500" />;
      case "login":
        return <User size={16} className="text-indigo-500" />;
      case "profile_updated":
        return <User size={16} className="text-orange-500" />;
      case "check_in":
        return <LogIn size={16} className="text-success" />;
      case "check_out":
        return <LogOut size={16} className="text-danger" />;
      default:
        return <AlertCircle size={16} className="text-gray-500" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "student":
        return "primary";
      case "admin":
        return "success";
      default:
        return "secondary";
    }
  };

  // Fetch last check-in status for tutor
  const fetchCheckInStatus = async () => {
    if (!user || !user.id) return;
    setCheckInLoading(true);
    try {
      const q = query(
        collection(db, "attendance"),
        where("userId", "==", user.id),
        where("role", "==", "tutor")
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        // Find all attendance records for today, in reverse chronological order
        const now = new Date();
        let foundToday = false;
        let lastType = null;
        for (const doc of snap.docs) {
          const data = doc.data();
          const recordDate = new Date(data.timestamp);
          if (
            recordDate.getFullYear() === now.getFullYear() &&
            recordDate.getMonth() === now.getMonth() &&
            recordDate.getDate() === now.getDate()
          ) {
            if (!foundToday) {
              setLastCheckIn(data);
              foundToday = true;
            }
            if (data.type === "check_in") {
              // If we find a check-in before a check-out, user is checked in
              lastType = "check_in";
              break;
            } else if (data.type === "check_out") {
              // If we find a check-out first, user is not checked in
              lastType = "check_out";
              break;
            }
          }
        }
        setIsCheckedIn(lastType === "check_in");
        if (!foundToday) {
          setLastCheckIn(null);
        }
      } else {
        setIsCheckedIn(false);
        setLastCheckIn(null);
      }
    } catch (e) {
      setIsCheckedIn(false);
      setLastCheckIn(null);
    }
    setCheckInLoading(false);
  };

  const handleCheckIn = async () => {
    if (!user || !user.id) return;
    setCheckInLoading(true);
    try {
      await addDoc(collection(db, "attendance"), {
        userId: user.id,
        role: "tutor",
        type: "check_in",
        timestamp: Date.now(),
      });
      // Log activity (include userRole for notification)
      await logActivity({
        userId: user.id,
        userRole: "tutor",
        text: `${user.name || user.email} checked in`,
        type: "attendance",
        actionType: "tutor_action",
        metadata: {
          userName: user.name || user.email,
          action: "check_in"
        }
      });
      await fetchCheckInStatus();
    } finally {
      setCheckInLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!user || !user.id) return;
    setCheckInLoading(true);
    try {
      await addDoc(collection(db, "attendance"), {
        userId: user.id,
        role: "tutor",
        type: "check_out",
        timestamp: Date.now(),
      });
      // Log activity (include userRole for notification)
      await logActivity({
        userId: user.id,
        userRole: "tutor",
        text: `${user.name || user.email} checked out`,
        type: "attendance",
        actionType: "tutor_action",
        metadata: {
          userName: user.name || user.email,
          action: "check_out"
        }
      });
      await fetchCheckInStatus();
    } finally {
      setCheckInLoading(false);
    }
  };

  const handleAddSkill = async () => {
    if (!skills.includes(newSkill)) {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        toast.error("User not found.");
        setLoading(false);
        return;
      }

      user["skills"] = [...skills, newSkill];
      const updatedSkill = await updateUser(user.id, user);
      localStorage.setItem("user", JSON.stringify(user));
      if (updatedSkill) {
        // Log activity (include userRole for notification)
        await logActivity({
          userId: user.id,
          userRole: "tutor",
          text: `${user.name || user.email} added skill: ${newSkill?.name || newSkill}`,
          type: "skill",
          actionType: "tutor_action",
          metadata: {
            skillName: newSkill?.name || newSkill,
            skillId: newSkill?.id || newSkill,
            userName: user.name || user.email,
            action: "add"
          }
        });

        setLoading(false);
        setSkills([...skills, newSkill]);
        setNewSkill("");
        toast.success("Skill added successfully.");

        // Refresh activities
        fetchRecentActivities();
      }
    } else {
      setLoading(false);
      toast.error("Skill is already added.");
    }
  };

  const handleRemoveSkill = async (skillId) => {
    if (!window.confirm("Are you sure you want to remove this skill?")) return;
    const updated = skills.filter((s) => s.id !== skillId);
    setSkills(updated);
    toast.success("Skill removed successfully.");
    // TODO: Update tutor in DB if needed
  };

  const handleQuickLeaveSubmit = async (e) => {
    e.preventDefault();
    if (!leaveFormData.startDate || !leaveFormData.endDate || !leaveFormData.reason.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (new Date(leaveFormData.startDate) > new Date(leaveFormData.endDate)) {
      toast.error("End date must be after start date");
      return;
    }
    setQuickLeaveLoading(true);
    try {
      const leaveData = {
        ...leaveFormData,
        userId: user.id,
        userName: user.name || user.email,
        userEmail: user.email,
        totalDays: calculateLeaveDays(leaveFormData.startDate, leaveFormData.endDate),
      };
      await applyForLeave(leaveData);
      // Log activity
      await logActivity({
        userId: user.id,
        userRole: "tutor",
        text: `${user.name || user.email} applied for leave (${leaveFormData.leaveType}) from ${leaveFormData.startDate} to ${leaveFormData.endDate}`,
        type: "leave",
        actionType: "tutor_action",
        metadata: {
          userName: user.name || user.email,
          action: "apply_leave",
          leaveType: leaveFormData.leaveType,
          startDate: leaveFormData.startDate,
          endDate: leaveFormData.endDate,
          reason: leaveFormData.reason
        }
      });
      setLeaveFormData({ leaveType: LEAVE_TYPES.PERSONAL, startDate: "", endDate: "", reason: "" });
      setShowQuickLeaveForm(false);
      await fetchLeaveData();
      toast.success("Leave application submitted.");
    } catch (error) {
      console.error("Error submitting leave application:", error);
      toast.error("Error submitting leave application");
    }
    setQuickLeaveLoading(false);
  };

  const handleCancelLeave = async (leaveId) => {
    setCancelingLeaveId(leaveId);
    try {
      await cancelLeave(leaveId);
      await fetchLeaveData();
      toast.success("Leave cancelled.");
    } catch (error) {
      console.error("Error cancelling leave:", error);
      toast.error("Error cancelling leave");
    }
    setCancelingLeaveId(null);
  };

  const handleLeaveFormChange = (e) => {
    const { name, value } = e.target;
    setLeaveFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="container py-4">
      <h2 className="text-2xl font-bold mb-4 text-center">Tutor Dashboard</h2>

      {/* Stats Cards Row */}
      <div className="row mb-4">
        <div className="col-lg-4 col-md-6 mb-3">
          <CompactStatsCard
            icon="✅"
            title="Tasks"
            total={tasks.length}
            primaryColor="#6366F1"
            stats={[
              {
                value: tasks.filter((t) => t.status === "pending").length,
                label: "Pending",
                colorClass: "text-danger",
              },
              {
                value: tasks.filter((t) => t.status === "in_progress").length,
                label: "Progress",
                colorClass: "text-warning",
              },
              {
                value: tasks.filter((t) => t.status === "completed").length,
                label: "Done",
                colorClass: "text-success",
              },
            ]}
          />
        </div>
        <div className="col-lg-4 col-md-6 mb-3">
          <CompactStatsCard
            icon="📂"
            title="Projects"
            total={projects.length}
            primaryColor="#8B5CF6"
            stats={[
              {
                value: projects.filter((p) => p.status === "planning").length,
                label: "Planning",
                colorClass: "text-warning",
              },
              {
                value: projects.filter((p) => p.status === "in_progress").length,
                label: "Progress",
                colorClass: "text-primary",
              },
              {
                value: projects.filter((p) => p.status === "completed").length,
                label: "Done",
                colorClass: "text-success",
              },
            ]}
          />
        </div>
        <div className="col-lg-4 col-md-6 mb-3">
          <CompactStatsCard
            icon="📚"
            title="Courses"
            total={courses.length}
            primaryColor="#06b6d4"
            stats={[
              {
                value: courses.length,
                label: "Assigned",
                colorClass: "text-info",
              },
            ]}
          />
        </div>
      </div>

      {/* Public Holidays Info Banner and Button Side by Side */}
      {(todayHoliday || upcomingHoliday) && (
        <div className="d-flex align-items-center mb-3" style={{ gap: '1rem', flexWrap: 'nowrap' }}>
          {todayHoliday && (
            <div className="alert alert-success mb-0 flex-grow-1 d-flex align-items-center" style={{ minHeight: '48px' }}>
              <span><strong>Today is a public holiday:</strong> <b>{todayHoliday.name}</b> ({todayHoliday.date}). Enjoy your holiday!</span>
            </div>
          )}
          {!todayHoliday && upcomingHoliday && (
            <div className="alert alert-info mb-0 flex-grow-1 d-flex align-items-center" style={{ minHeight: '48px' }}>
              <span><strong>Info:</strong> Tomorrow is <b>{upcomingHoliday.name}</b> ({upcomingHoliday.date}). Enjoy your holiday!</span>
            </div>
          )}
          <Button
            variant="outline-info"
            size="sm"
            className="ms-auto"
            style={{ minWidth: '170px', whiteSpace: 'nowrap' }}
            onClick={() => window.location.href = "/holidays"}
          >
            View Public Holidays
          </Button>
        </div>
      )}
      {/* Check-in/out section */}
      <div className="d-flex justify-content-center align-items-center mb-4 gap-3 flex-wrap">
        <button
          className="btn btn-success"
          onClick={handleCheckIn}
          disabled={isCheckedIn || checkInLoading}
        >
          {checkInLoading ? "Checking in..." : "Check In"}
        </button>
        <button
          className="btn btn-danger"
          onClick={handleCheckOut}
          disabled={!isCheckedIn || checkInLoading}
        >
          {checkInLoading ? "Checking out..." : "Check Out"}
        </button>
        {lastCheckIn && (
          <span className="text-muted text-sm ms-3">
            Last: {lastCheckIn.type === "check_in" ? "Checked in" : "Checked out"} {formatDistanceToNow(new Date(lastCheckIn.timestamp), { addSuffix: true })}
          </span>
        )}
      </div>

      {/* Removed old StatsCard row as CompactStatsCard cards are now used above */}
      {/* Monthly Attendance Table */}
      {renderMonthlyAttendanceTable()}

      {/* Skills Section */}
      <div className="mb-4">
        <Card className="shadow">
          <Card.Body>
            <Card.Title className="mb-3">Skills ({skills.length})</Card.Title>
            <div className="flex justify-between mb-3">
              <Form.Select
                value={newSkill?.id || ""}
                onChange={e => {
                  const selected = allSkills.find(skill => skill.id === e.target.value);
                  setNewSkill(selected);
                }}
                style={{ maxWidth: 200 }}
              >
                <option value="" disabled>Select</option>
                {allSkills.map(skill => (
                  <option key={skill.id} value={skill.id}>{skill.name}</option>
                ))}
              </Form.Select>
              <Button onClick={handleAddSkill}>Add</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2 mb-3">
              {skills.map((skill, idx) => (
                <Badge
                  key={idx}
                  bg="info"
                  className="p-2 flex items-center gap-2"
                  onClick={() => handleRemoveSkill(skill?.id)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="flex items-center gap-2">
                    <span>{skill?.name}</span>
                    <XIcon className="w-[18px] h-[18px] p-1 bg-blue-700 rounded-full cursor-pointer hover:bg-blue-900" title="Remove Skill" />
                  </div>
                </Badge>
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Apply Leave Section */}
      <div className="mb-4">
        <Card className="shadow">
          <Card.Body>
            <Card.Title className="mb-3 d-flex justify-content-between align-items-center">
              <span>Apply for Leave</span>
              <Button
                size="sm"
                variant={showQuickLeaveForm ? "outline-secondary" : "primary"}
                onClick={() => setShowQuickLeaveForm(!showQuickLeaveForm)}
              >
                {showQuickLeaveForm ? "Cancel" : "Apply Leave"}
              </Button>
            </Card.Title>
            <div className="row">
              <div className="col-md-6 border-end">
                {showQuickLeaveForm ? (
                  <Form onSubmit={handleQuickLeaveSubmit}>
                    <div className="row mb-3">
                      <div className="col-md-12">
                        <Form.Label>Leave Type</Form.Label>
                        <Form.Select
                          name="leaveType"
                          value={leaveFormData.leaveType}
                          onChange={handleLeaveFormChange}
                          required
                        >
                          <option value={LEAVE_TYPES.PERSONAL}>Personal</option>
                          <option value={LEAVE_TYPES.SICK}>Sick Leave</option>
                          <option value={LEAVE_TYPES.VACATION}>Vacation</option>
                          <option value={LEAVE_TYPES.EMERGENCY}>Emergency</option>
                          <option value={LEAVE_TYPES.OTHER}>Other</option>
                        </Form.Select>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={leaveFormData.startDate}
                          onChange={handleLeaveFormChange}
                          min={new Date().toISOString().split("T")[0]}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          name="endDate"
                          value={leaveFormData.endDate}
                          onChange={handleLeaveFormChange}
                          min={leaveFormData.startDate || new Date().toISOString().split("T")[0]}
                          required
                        />
                      </div>
                    </div>
                    {leaveFormData.startDate && leaveFormData.endDate && (
                      <div className="mb-3">
                        <Form.Label>Total Days</Form.Label>
                        <div className="form-control-plaintext">
                          <Badge bg="info">
                            {calculateLeaveDays(leaveFormData.startDate, leaveFormData.endDate)} days
                          </Badge>
                        </div>
                      </div>
                    )}
                    <Form.Group className="mb-3">
                      <Form.Label>Reason</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="reason"
                        value={leaveFormData.reason}
                        onChange={handleLeaveFormChange}
                        placeholder="Please provide a reason for your leave..."
                        required
                      />
                    </Form.Group>
                    <Button type="submit" variant="success" className="me-2" disabled={quickLeaveLoading}>
                      {quickLeaveLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit"
                      )}
                    </Button>
                    <Button type="button" variant="outline-secondary" onClick={() => setShowQuickLeaveForm(false)} disabled={quickLeaveLoading}>
                      Cancel
                    </Button>
                  </Form>
                ) : (
                  <div className="text-center text-muted py-4">
                    <p>Click "Apply Leave" to submit a quick leave application.</p>
                    <small>For detailed applications, visit the main Leaves page.</small>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <h6>Recent Leave Applications</h6>
                {userLeaves.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <p>No leave applications found.</p>
                    <small>Your leave history will appear here.</small>
                  </div>
                ) : (
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {userLeaves.slice(0, 5).map((leave) => (
                      <div key={leave.id} className="border-bottom pb-2 mb-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong className="text-capitalize">{leave.leaveType.replace("_", " ")}</strong><br />
                            <small className="text-muted">{new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}</small><br />
                            <small className="text-info">{calculateLeaveDays(leave.startDate, leave.endDate)} days</small>
                          </div>
                          <div className="text-end">
                            <Badge
                              bg={leave.status === LEAVE_STATUS.APPROVED ? "success" : leave.status === LEAVE_STATUS.REJECTED ? "danger" : leave.status === LEAVE_STATUS.PENDING ? "warning" : "secondary"}
                              className="mb-1"
                            >
                              {leave.status}
                            </Badge>
                            {leave.status === LEAVE_STATUS.PENDING && (
                              <div>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleCancelLeave(leave.id)}
                                  disabled={cancelingLeaveId === leave.id}
                                >
                                  {cancelingLeaveId === leave.id ? (
                                    <div className="flex items-center gap-1">
                                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Canceling...
                                    </div>
                                  ) : (
                                    "Cancel"
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="mb-1 small text-muted">{leave.reason.length > 60 ? leave.reason.substring(0, 60) + "..." : leave.reason}</p>
                        {leave.adminComments && (
                          <div className="mt-1">
                            <small className="text-success"><strong>Admin:</strong> {leave.adminComments}</small>
                          </div>
                        )}
                      </div>
                    ))}
                    {userLeaves.length > 5 && (
                      <small className="text-muted">Showing latest 5 applications. <a href="/leaves">View all</a></small>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* College Statistics Section */}
      <div className="mb-4">
        <Card className="shadow">
          <Card.Body>
            <Card.Title className="d-flex align-items-center gap-2 mb-3">
              🏫 College Partnerships Overview
            </Card.Title>
            {collegeLoading ? (
              <div className="text-center py-3">
                <Loader />
              </div>
            ) : collegeStats ? (
              <Row>
                <Col md={3} className="mb-3 mb-md-0">
                  <SalaryCard
                    title="Total Colleges"
                    value={collegeStats.totalColleges}
                    variant="primary"
                    icon="building"
                  />
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <SalaryCard
                    title="Active Partnerships"
                    value={collegeStats.activeColleges}
                    variant="success"
                    icon="check-circle"
                  />
                </Col>
                <Col md={3} className="mb-3 mb-md-0">
                  <SalaryCard
                    title="Students Assigned"
                    value={collegeStats.totalStudentsAssigned}
                    variant="info"
                    icon="users"
                  />
                </Col>
                <Col md={3}>
                  <SalaryCard
                    title="Avg Students/College"
                    value={Math.round(collegeStats.averageStudentsPerCollege)}
                    variant="warning"
                    icon="chart-bar"
                  />
                </Col>
              </Row>
            ) : (
              <div className="text-center text-muted py-3">
                No college data available
              </div>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Recent Activities Section */}
      <div className="mb-4">
        <Card className="shadow">
          <Card.Body>
            <Card.Title className="d-flex align-items-center gap-2">
              <Clock size={20} className="text-primary" />
              Recent Activities (Last 7)
            </Card.Title>
            {activitiesLoading ? (
              <div className="text-center py-3">
                <Loader />
              </div>
            ) : recentActivities.length === 0 ? (
              <p className="text-center text-muted py-3">
                No recent activities found.
              </p>
            ) : (
              <ListGroup variant="flush">
                {recentActivities.map((activity, index) => (
                  <ListGroup.Item key={activity.id} className="border-0 py-3">
                    <div className="d-flex align-items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <div className="d-flex align-items-center gap-2">
                            <strong className="text-sm">
                              {activity.userName}
                            </strong>
                            <Badge
                              bg={getRoleBadgeColor(activity.userRole)}
                              className="text-xs"
                            >
                              {activity.userRole}
                            </Badge>
                          </div>
                          <small className="text-muted">
                            {activity.createdAt ? (
                              formatDistanceToNow(new Date(activity.createdAt), {
                                addSuffix: true,
                              })
                            ) : (
                              <span className="text-warning">Just now</span>
                            )}
                          </small>
                        </div>
                        <p className="mb-0 text-muted small">{activity.text}</p>
                      </div>
                    </div>
                    {index < recentActivities.length - 1 && (
                      <hr className="mt-3 mb-0 opacity-25" />
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Card.Body>
        </Card>
      </div>

      <TutorChart />
    </div>
  );
}
